import type { FileWithLatestFileVersion } from "schemas/generated/file";

import * as React from "react";

import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";

import { DeploymentStatusTag } from "@/features/react-editor/components/DeploymentStatusTag";
import { EditablePageName } from "@/features/react-editor/components/navbar/EditablePageName";
import usePublishedSiteUrls from "@/features/react-editor/hooks/usePublishedSiteUrls";
import useShopDomain from "@/features/react-editor/hooks/useShopDomain";
import { useIsMobile } from "@replo/design-system/components/shadcn/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { Link } from "react-router-dom";

import { DevicePreview } from "../DevicePreview";
import PageCardAnalytics from "./PageCardAnalytics";
import PageCardMenu from "./PageCardMenu";

const CARD_CONSTANTS = {
  PREVIEW_HEIGHT: 155,
  MOBILE_MAX_WIDTH: 140,
  MIN_CARD_WIDTH: 300,
} as const;

const PageCard = ({
  file,
  layoutClassName,
}: {
  file: FileWithLatestFileVersion;
  layoutClassName?: string;
}) => {
  const { project } = useCurrentProjectContext();

  const [isRenamingPage, setIsRenamingPage] = React.useState(false);

  const isPublished = Boolean(file.lastPublishedFileVersionId);

  const publishedSiteUrls = usePublishedSiteUrls({
    pageRoutesToUseForPreview: [file.pageRoute ?? ""],
  });

  const publishedSiteUrl = publishedSiteUrls[0];
  const shopDomain = useShopDomain();
  const isMobile = useIsMobile();

  if (!project) {
    return null;
  }

  return (
    <div className="h-full">
      <Link to={`/shops/${project.id}/chat/?pageId=${file.id}`}>
        <div
          className={twMerge(
            "border-0.5 border-border rounded-lg bg-sheet hover:bg-menu-item-hover flex flex-col cursor-pointer shadow-sm transition-all duration-200 h-full hover:scale-[0.99] hover:shadow-lg",
            layoutClassName,
          )}
          style={{
            minWidth: isMobile
              ? CARD_CONSTANTS.MOBILE_MAX_WIDTH
              : CARD_CONSTANTS.MIN_CARD_WIDTH,
          }}
        >
          <div className="p-4 pb-0 flex flex-col gap-2">
            <div
              className="flex justify-center items-start gap-4 overflow-hidden"
              style={{ height: `${CARD_CONSTANTS.PREVIEW_HEIGHT}px` }}
            >
              <DevicePreview
                pageRoute={file.pageRoute ?? ""}
                className="w-full h-full pointer-events-none"
                isScrollable={false}
                hideScrollbars={true}
                publishedSiteUrl={publishedSiteUrl}
                domain={!publishedSiteUrl ? shopDomain : undefined}
                mobileClassName="pointer-events-none h-full w-full"
                maxMobileWidth={CARD_CONSTANTS.MOBILE_MAX_WIDTH}
                desktopClassName="pointer-events-none h-full w-full border-l-[1px] border-r-[1px] border-t-[1px] border-border/25 rounded-t-md"
              />
            </div>
          </div>

          <div className="px-4 py-3 flex flex-col gap-2 border-t-0.5 border-border bg-white rounded-b-lg">
            <div className="flex justify-between items-center gap-2">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {isRenamingPage ? (
                  <EditablePageName
                    page={file}
                    projectId={project.id}
                    setIsRenaming={setIsRenamingPage}
                    size="sm"
                  />
                ) : (
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsRenamingPage(true);
                    }}
                    className="header-h4 text-default truncate min-w-0"
                    title={file.name}
                  >
                    {file.name}
                  </div>
                )}

                <DeploymentStatusTag file={file} />
              </div>

              <div className="shrink-0">
                <PageCardMenu
                  page={file}
                  isPublished={isPublished}
                  publishedSiteUrl={publishedSiteUrl}
                  setIsRenamingPage={setIsRenamingPage}
                />
              </div>
            </div>

            <PageCardAnalytics
              file={file}
              publishedSiteUrl={publishedSiteUrl}
            />
          </div>
        </div>
      </Link>
    </div>
  );
};

export default PageCard;
